import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { useOutletContext } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useTabParam } from '@structural-climbing/ui'
import { ChipTabs } from '../design/ChipTabs'
import type { NewsItem } from '@structural-climbing/ui'
import { useNews } from '../features/news/useNews'
import { splitFeed, liveEventCount } from '../features/news/newsSelectors'
import { partitionEvents, nextEventId } from '../features/news/sortNews'
import { NewsCard, TabTitle, type JoinHandlers } from '../features/news/NewsCard'
import { Page } from '../design/Page'
import { EmptyState } from '../design/EmptyState'
import { SkeletonCard } from '../design/Skeleton'
import { staggerIndex } from '../design/motion'
import type { GymContext } from '../layouts/GymLayout'

const EVENT_TABS = ['gym', 'community'] as const

// How many finished events stay on the page. The list only grows, and nobody
// scrolls back through two years of workshops.
const PAST_LIMIT = 10

function EventsList({
  items,
  emptyLabel,
  join,
}: {
  items: NewsItem[]
  emptyLabel: string
  join?: JoinHandlers
}) {
  const { t } = useTranslation()
  const { upcoming, past } = partitionEvents(items)
  const nextId = nextEventId(items)

  if (items.length === 0) {
    return <EmptyState icon="📅" title={emptyLabel} />
  }

  return (
    <div className="flex flex-col gap-6">
      {upcoming.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="eyebrow px-1 text-foreground/40">{t('events.upcomingHeading')}</h2>
          {upcoming.map((item, i) => (
            <motion.div key={item.id} {...staggerIndex(i)}>
              <NewsCard item={item} isNext={item.id === nextId} join={join} />
            </motion.div>
          ))}
        </section>
      )}
      {past.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="eyebrow px-1 text-foreground/40">{t('events.pastHeading')}</h2>
          {/* Past events keep their card but lose the RSVP button: passing no
              join handlers is what makes them read-only. */}
          <div className="flex flex-col gap-3 opacity-60">
            {past.slice(0, PAST_LIMIT).map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

export function EventsPage() {
  const { t } = useTranslation()
  const { gymId, gymName } = useOutletContext<GymContext>()
  const { gym, global, loading, joinEvent, leaveEvent } = useNews(gymId)
  const [tab, setTab] = useTabParam('tab', EVENT_TABS, 'gym')

  const { gymEvents, communityEvents } = useMemo(() => splitFeed({ gym, global }), [gym, global])

  // Nothing is marked seen here. Opening this page is not the same as the event
  // having happened, and only the latter retires an event's dot — so a workshop
  // on Saturday keeps announcing itself from the tab bar until Saturday.

  return (
    <Page>
      <h1 className="sr-only">{t('events.pageTitle')}</h1>
      {loading ? (
        <div className="flex flex-col gap-3">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <ChipTabs
            aria-label={t('events.pageTitle')}
            value={tab}
            onChange={setTab}
            options={[
              {
                key: 'gym',
                label: <TabTitle label={t('events.tabGym')} dot={liveEventCount(gymEvents) > 0} />,
              },
              {
                key: 'community',
                label: (
                  <TabTitle
                    label={t('events.tabCommunity')}
                    dot={liveEventCount(communityEvents) > 0}
                  />
                ),
              },
            ]}
          />
          <div role="tabpanel" aria-label={t('events.pageTitle')}>
            {tab === 'gym' ? (
              <EventsList
                items={gymEvents}
                emptyLabel={t('events.emptyGym', { gym: gymName })}
                join={{ onJoin: joinEvent, onLeave: leaveEvent }}
              />
            ) : (
              // Community events are announcements: they carry no seats and
              // cannot be joined, so no RSVP handlers are passed.
              <EventsList items={communityEvents} emptyLabel={t('events.emptyCommunity')} />
            )}
          </div>
        </div>
      )}
    </Page>
  )
}
